// https://stackoverflow.com/questions/62210286/declare-type-with-react-useimperativehandle
// https://github.com/jaredpalmer/formik/issues/1603#issuecomment-575669249

import { Alert, AlertIcon, Stack, Text, useToast } from "@chakra-ui/react";
import React from "react";
import * as Yup from "yup";
import { LibFullWidthSpinner } from "../../../components/lib";
import { ColumnKeyEnum } from "../../../constants";

import { useCreateRegistryRecord } from "../../../services/api/data/mutations/useCreateRegistryRecord";
import useReadRegistrySchema from "../../../services/api/schemas/queries/useReadRegistrySchema";
import { GeneialFieldVersionTypeEnum } from "../models/GeneialFieldVersion";
import { CreateRegistryRecordForm } from "./CreateRegistryRecordForm";

export const ADD_RECORD_EXCLUDE_FIELDS = [ColumnKeyEnum.record_id.toString()];

interface IProps {
  registryId: string;
  onClose: () => void;
}

export const CreateRegistryRecordContainer = ({
  registryId,
  onClose,
}: IProps) => {
  const [isReady, setIsReady] = React.useState<boolean>(false);

  const [registryFormSchema, setRegistryFormSchema] = React.useState<any>({});
  const [workingFormValues, setWorkingFormValues] = React.useState<any>();

  const toast = useToast();

  const {
    data: dataRegistrySchema,
    isLoading: isLoadingRegistrySchema,
    isError: isErrorReadSchema,
  } = useReadRegistrySchema(registryId);

  const {
    mutateAsync: mutateAsyncCreate,
    isLoading: isLoadingCreate,
    isError: isErrorCreate,
  } = useCreateRegistryRecord();

  const handleOnSubmit = async (createData) => {
    try {
      await mutateAsyncCreate({ registryId, createData });
      toast({
        title: "Created record",
        description: `Data is being refreshed now..`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      onClose();
    } catch (e) {
      toast({
        title: "Failed to create",
        description: `Please let us know and we will fix this.`,
        status: "error",
        duration: 2000,
        isClosable: true,
      });
      console.log(e);
    }
  };

  React.useEffect(() => {
    // Build yup schema obj
    if (dataRegistrySchema) {
      const schemaShapeUpdate = {};
      const workingFormValuesUpdate = {};

      dataRegistrySchema.schemas.forEach((schemaNode) => {
        schemaNode.fields?.forEach((schemaField) => {
          if (
            ADD_RECORD_EXCLUDE_FIELDS.indexOf(schemaField.field_key) >= 0 ||
            schemaField.is_excluded ||
            schemaField.field_type === GeneialFieldVersionTypeEnum.calc ||
            schemaField.field_type === GeneialFieldVersionTypeEnum.calc_on_read ||
            schemaField.field_type === GeneialFieldVersionTypeEnum.calc_on_write
          ) {
            return;
          }

          let schemaFieldValidator = Yup.string();

          if (schemaField.is_required) {
            schemaFieldValidator = schemaFieldValidator.required("This field is required");
          }
          if (schemaField.validation != null) {
            if (schemaField.validation === "date_ymd") {
              const reg = /^\d{4}-\d{2}-\d{2}$/;
              schemaFieldValidator = schemaFieldValidator.matches(reg, "Field is not in correct format");
            } 
            else if (schemaField.validation === "date_ymd_hms") {
              const reg = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
              schemaFieldValidator = schemaFieldValidator.matches(reg, "Field is not in correct format");
            }
            else {
              const reg = new RegExp(schemaField.validation)
              schemaFieldValidator = schemaFieldValidator.matches(reg, "Field is not in correct format");
            }
          }

          if (schemaField.field_type === GeneialFieldVersionTypeEnum.checkbox) {
            // Unwrap checkbox fields, they dont use the straight field name
            schemaField.options?.forEach((option) => {
              if (option.lookup_key) {
                workingFormValuesUpdate[option.lookup_key] = "";
                // if (schemaField.is_required) {
                //   schemaShapeUpdate[option.lookup_key] = Yup.string().required("This field is required");
                // } else {
                //   schemaShapeUpdate[option.lookup_key] = Yup.string();
                // }
                schemaShapeUpdate[option.lookup_key] = Yup.string();
              }
            });
          } else if (
            schemaField.field_type === GeneialFieldVersionTypeEnum.radio ||
            schemaField.field_type === GeneialFieldVersionTypeEnum.yesno
          ) {
            // workingFormValuesUpdate[schemaField.field_key] = "1";

            if (schemaField.is_required) {
              schemaShapeUpdate[schemaField.field_key] = Yup.string().required("This field is required");
            } else {
              schemaShapeUpdate[schemaField.field_key] = Yup.string();
            }

          } else if (
            schemaField.field_type === GeneialFieldVersionTypeEnum.dropdown
          ) {
            if (schemaField.options && schemaField.options.length > 0) {
              // workingFormValuesUpdate[schemaField.field_key] =
              //   schemaField.options[0].value;

              if (schemaField.is_required) {
                schemaShapeUpdate[schemaField.field_key] = Yup.string().required("This field is required");
              } else {
                schemaShapeUpdate[schemaField.field_key] = Yup.string();
              }

            }
          } else {
            workingFormValuesUpdate[schemaField.field_key] = "";

            schemaShapeUpdate[schemaField.field_key] = schemaFieldValidator;
          }
        });
      });

      const schemaObjUpdate = Yup.object().shape(schemaShapeUpdate);

      setRegistryFormSchema(schemaObjUpdate);
      setWorkingFormValues(workingFormValuesUpdate);
      setIsReady(true);
    }
  }, [dataRegistrySchema]);

  return (
    <Stack spacing={6}>
      {isLoadingRegistrySchema && (
        <LibFullWidthSpinner size="md" thickness="2px" />
      )}

      {isErrorReadSchema && (
        <Alert status="error" variant={"solid"}>
          <AlertIcon />
          <Text>Failed to load schema for creating a new record.</Text>
        </Alert>
      )}

      {isReady && dataRegistrySchema && !isErrorReadSchema && (
        <CreateRegistryRecordForm
          validationSchema={registryFormSchema}
          initialValues={workingFormValues}
          registrySchema={dataRegistrySchema}
          onSubmit={handleOnSubmit}
          isLoading={isLoadingCreate}
          onClose={onClose}
        ></CreateRegistryRecordForm>
      )}

      {isErrorCreate && (
        <Alert status="error" variant={"solid"}>
          <AlertIcon />
          <Text>Failed to create new record.</Text>
        </Alert>
      )}
    </Stack>
  );
};
